import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

/**
 * Keyboard Context and Hook
 * 
 * This context manages keyboard visibility state across the entire app with a single
 * set of event listeners, rather than creating duplicate listeners for each component
 * that uses the hook. This is more efficient when multiple components need to track
 * keyboard visibility.
 * 
 * Usage:
 * 1. Wrap your app with KeyboardProvider (already done in index.tsx)
 * 2. Use the useKeyboard() hook in any component that needs keyboard state
 */

interface KeyboardContextType {
  isKeyboardVisible: boolean;
}

const KeyboardContext = createContext<KeyboardContextType | null>(null);

export const KeyboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let keyboardShowListener: any;
    let keyboardHideListener: any;

    const setupListeners = async () => {
      keyboardShowListener = await Keyboard.addListener('keyboardWillShow', () => {
        setIsKeyboardVisible(true);
      });

      keyboardHideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyboardVisible(false);
      });
    };

    setupListeners();

    return () => {
      keyboardShowListener?.remove();
      keyboardHideListener?.remove();
    };
  }, []);

  const value = {
    isKeyboardVisible,
  };

  return <KeyboardContext.Provider value={value}>{children}</KeyboardContext.Provider>;
};

export const useKeyboard = () => {
  const context = useContext(KeyboardContext);
  if (!context) {
    throw new Error('useKeyboard must be used within a KeyboardProvider');
  }
  return context;
};
